import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Description as DocumentIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import api from '../../utils/api';

function DocumentRow({ document }) {
  const [open, setOpen] = useState(false);
  
  const completionPercentage = document.completion_percentage || 0;
  const processingPercentage = document.processing?.latex_formatter
    ? (document.processing.latex_formatter / document.total_segments) * 100
    : 0;
  const reviewPercentage = document.review?.fully_verified
    ? (document.review.fully_verified / document.review.total_segments) * 100
    : 0;

  const getStatusChip = () => {
    if (completionPercentage === 100) {
      return <Chip label="Complete" color="success" size="small" />;
    } else if (processingPercentage < 100) {
      return <Chip label="Processing" color="warning" size="small" />;
    } else if (reviewPercentage < 100) {
      return <Chip label="Needs Review" color="info" size="small" />;
    }
    return <Chip label="In Progress" color="default" size="small" />;
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Box display="flex" alignItems="center" gap={1}>
            <DocumentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body2">{document.name}</Typography>
          </Box>
        </TableCell>
        <TableCell align="center">{document.total_segments}</TableCell>
        <TableCell align="center">{getStatusChip()}</TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={completionPercentage} 
                sx={{ height: 8, borderRadius: 1 }}
                color={completionPercentage === 100 ? 'success' : 'primary'}
              />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {`${Math.round(completionPercentage)}%`}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          {new Date(document.created_at).toLocaleDateString()}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Document Details
              </Typography>
              
              <Grid container spacing={3}>
                {/* Processing Details */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Processing Status
                      </Typography>
                      {[
                        { label: 'Separator', value: document.processing.separator },
                        { label: 'Categorizer', value: document.processing.categorizer },
                        { label: 'Difficulty Rater', value: document.processing.difficulty_rater },
                        { label: 'Answer Generator', value: document.processing.answer_generator },
                        { label: 'LaTeX Formatter', value: document.processing.latex_formatter },
                      ].map((item) => (
                        <Box key={item.label} sx={{ mb: 1 }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">{item.label}</Typography>
                            <Typography variant="caption">
                              {item.value} / {document.total_segments}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={(item.value / document.total_segments) * 100} 
                            sx={{ height: 4, borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Review Details */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Review Status
                      </Typography>
                      {document.review.total_segments > 0 ? (
                        <>
                          {[
                            { label: 'Topic Verified', value: document.review.topic_verified },
                            { label: 'Answer Verified', value: document.review.answer_verified },
                            { label: 'Template Verified', value: document.review.template_verified },
                            { label: 'Fully Verified', value: document.review.fully_verified },
                          ].map((item) => (
                            <Box key={item.label} sx={{ mb: 1 }}>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="caption">{item.label}</Typography>
                                <Typography variant="caption">
                                  {item.value} / {document.review.total_segments}
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={(item.value / document.review.total_segments) * 100} 
                                sx={{ height: 4, borderRadius: 1 }}
                                color={item.label === 'Fully Verified' ? 'success' : 'primary'}
                              />
                            </Box>
                          ))}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No segments ready for review yet
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function DocumentMetricsList() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setPage(0); // Reset to first page on search
    }, 500),
    []
  );

  useEffect(() => {
    fetchDocuments();
  }, [page, rowsPerPage, sortBy, sortOrder, statusFilter, searchQuery]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      if (statusFilter !== 'all') {
        params.status_filter = statusFilter;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/api/tutor/dashboard/documents', { params });
      setDocuments(response.data.documents);
      setTotalCount(response.data.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch document metrics:', err);
      setError('Failed to load document metrics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchDocuments();
  };

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(0);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  if (loading && documents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Document Processing Breakdown</Typography>
          <Box display="flex" gap={2} alignItems="center">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search documents..."
              value={searchInput}
              onChange={handleSearchInputChange}
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Documents</MenuItem>
              <MenuItem value="needs_processing">Needs Processing</MenuItem>
              <MenuItem value="needs_review">Needs Review</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
            </TextField>
            
            <TextField
              select
              size="small"
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="created_at">Date Created</MenuItem>
              <MenuItem value="name">Document Name</MenuItem>
              <MenuItem value="total_segments">Total Segments</MenuItem>
              <MenuItem value="completion_percentage">Completion %</MenuItem>
            </TextField>

            <IconButton onClick={toggleSortOrder} size="small">
              <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}>
                {sortOrder === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}
              </Tooltip>
            </IconButton>

            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Statistics */}
        {documents.length > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Total Documents
                </Typography>
                <Typography variant="h6">{totalCount}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Needs Processing
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {documents.filter(d => d.processing?.latex_formatter < d.total_segments).length}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Needs Review
                </Typography>
                <Typography variant="h6" color="info.main">
                  {documents.filter(d => 
                    d.review?.total_segments > 0 && 
                    d.review?.fully_verified < d.review?.total_segments
                  ).length}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="h6" color="success.main">
                  {documents.filter(d => d.completion_percentage === 100).length}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        <TableContainer>
          <Table aria-label="document metrics table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Document Name</TableCell>
                <TableCell align="center">Segments</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Completion</TableCell>
                <TableCell align="center">Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <DocumentRow key={document.id} document={document} />
              ))}
              {documents.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">No documents found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </Card>
  );
}